import { useEffect } from 'react';
import { Music, Play, Pause, X, SkipBack, SkipForward, Volume2, VolumeX } from "lucide-react";

const C = { cream: '#fdf8f0', cream2: '#f5f0e4', gold: '#f0bf6b', brown: '#55381f', dark: '#2a2118', white: '#ffffff' };
const BORDER = `2px solid ${C.dark}`;

export function NowPlayingBar({ playingSong, isPlaying, currentTime, duration, waveformPeaks, onPlay, onStop, onSkipBack, onSkipForward, onSeekTo, volume, isMuted, onVolumeChange, onToggleMute, sidebarCollapsed, sidebarOffset }) {
  // Keyboard shortcuts: Space = play/pause, ← = back 10s, → = forward 10s
  useEffect(() => {
    if (!playingSong) return;
    const handleKey = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable) return;
      if (e.code === 'Space') { e.preventDefault(); onPlay(playingSong); }
      else if (e.code === 'ArrowLeft') { e.preventDefault(); onSkipBack(10); }
      else if (e.code === 'ArrowRight') { e.preventDefault(); onSkipForward(10); }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [playingSong, onPlay, onSkipBack, onSkipForward]);

  if (!playingSong) return null;

  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleSeekClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    onSeekTo(((e.clientX - rect.left) / rect.width) * duration);
  };

  const btnBase = { background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 6, borderRadius: 6, color: C.brown };

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: typeof sidebarOffset === 'number' ? sidebarOffset : (sidebarCollapsed ? 96 : 240),
      right: 0,
      background: C.cream2,
      borderTop: BORDER,
      zIndex: 100,
      transition: 'left 0.2s ease',
      fontFamily: "'Roboto', sans-serif",
    }}>
      {/* Waveform seekbar */}
      <div
        onClick={handleSeekClick}
        title="Click to seek"
        style={{
          width: '100%', height: 28,
          background: C.cream,
          borderBottom: `1px solid rgba(42,33,24,0.12)`,
          cursor: 'pointer',
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
          padding: '3px 0 0',
          overflow: 'hidden',
        }}
      >
        {Array.from({ length: 100 }).map((_, i) => {
          const barPct = ((i + 1) / 100) * 100;
          const isPast = barPct <= progress;
          const seed = Math.sin(i * 12.9898 + 78.233) * 43758.5453;
          const frac = seed - Math.floor(seed);
          const barH = waveformPeaks
            ? (waveformPeaks[Math.floor(i * waveformPeaks.length / 100)] || 0.1) * 100
            : 25 + frac * 75;
          return (
            <div key={i} style={{
              flex: 1,
              height: `${Math.max(barH, 6)}%`,
              borderRadius: 1,
              background: isPast ? C.gold : 'rgba(42,33,24,0.18)',
              transition: 'background 0.08s',
            }} />
          );
        })}
        {/* Playhead */}
        <div style={{
          position: 'absolute', left: `${progress}%`, top: 0, bottom: 0,
          width: 2, background: C.dark,
          transition: 'left 0.1s linear', zIndex: 1,
          boxShadow: `0 0 4px ${C.dark}60`,
        }} />
      </div>

      {/* Controls row */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '7px 16px', gap: 12, height: 58 }}>

        {/* Cover art + song info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          <div style={{
            width: 40, height: 40, flexShrink: 0,
            background: C.cream, border: BORDER, borderRadius: 6,
            overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {playingSong.cover_url
              ? <img src={playingSong.cover_url} alt={playingSong.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              : <Music size={16} color={C.brown} style={{ opacity: 0.45 }} />
            }
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: "'GeistPixel', monospace", fontSize: 12, fontWeight: 700, color: C.dark, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {playingSong.title}
            </div>
            {playingSong.composer_name && (
              <div style={{ fontSize: 12, color: C.brown, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {playingSong.composer_name}
              </div>
            )}
          </div>
        </div>

        {/* Playback controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          <button onClick={() => onSkipBack(10)} style={btnBase} title="Back 10s (←)">
            <SkipBack size={14} />
            <span style={{ fontSize: 12, fontWeight: 800, fontFamily: "'GeistPixel', monospace", marginLeft: 1 }}>10</span>
          </button>

          <button
            onClick={() => onPlay(playingSong)}
            title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            style={{
              width: 38, height: 38, borderRadius: '50%',
              background: C.gold, border: BORDER,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0, marginLeft: 2, marginRight: 2,
            }}
          >
            {isPlaying
              ? <Pause size={15} color={C.dark} />
              : <Play size={15} color={C.dark} fill={C.dark} style={{ marginLeft: 2 }} />
            }
          </button>

          <button onClick={() => onSkipForward(10)} style={btnBase} title="Forward 10s (→)">
            <span style={{ fontSize: 12, fontWeight: 800, fontFamily: "'GeistPixel', monospace", marginRight: 1 }}>10</span>
            <SkipForward size={14} />
          </button>
        </div>

        {/* Time */}
        <div style={{
          fontFamily: "'GeistPixel', monospace", fontSize: 12, color: C.brown,
          whiteSpace: 'nowrap', minWidth: 76, textAlign: 'center', flexShrink: 0,
        }}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>

        {/* Volume */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
          <button onClick={onToggleMute} style={btnBase} title={isMuted ? 'Unmute' : 'Mute'}>
            {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
          </button>
          <input
            type="range" min="0" max="1" step="0.05"
            value={isMuted ? 0 : volume}
            onChange={e => onVolumeChange(parseFloat(e.target.value))}
            style={{ width: 64, accentColor: C.gold, cursor: 'pointer' }}
            aria-label="Volume"
          />
        </div>

        {/* Close */}
        <button onClick={onStop} style={{ ...btnBase, marginLeft: 4 }} title="Stop playback">
          <X size={15} />
        </button>
      </div>
    </div>
  );
}
